import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/hooks/useLogout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, LogOut, Mail, User } from "lucide-react";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { ProfileEditor } from "./ProfileEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useThemeStore } from "@/store/themeStore";
import { StatusReportPickerRow } from "@/components/settings/StatusReportPickerRow";
import { QuoteViewNotificationsRow } from "@/components/settings/QuoteViewNotificationsRow";

/**
 * Personal settings panel displaying account information and session controls.
 * Follows the standard settings UX pattern with section labels, settings rows,
 * and skeleton loading state.
 * @returns Personal settings panel element
 */
export function PersonalSettingsPanel() {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const { logout, isLoggingOut } = useLogout();
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  const handleThemeChange = (value: string) => {
    if (value === "light" || value === "dark" || value === "system") {
      setTheme(value);
    }
  };

  if (isLoading) {
    return <PersonalSettingsSkeleton />;
  }

  if (!user) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          Unable to load account information.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* ── Account ── */}
      <SectionLabel title="Account" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SettingsRow
            id="personal-profile"
            label="Profile"
            description="Your name and profile picture across the platform."
          >
            <ProfileEditor />
          </SettingsRow>

          <SettingsRow
            id="personal-email"
            label="Email"
            description="The email address associated with your account."
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-3.5 w-3.5" />
              <span>{user.email}</span>
            </div>
          </SettingsRow>

          <SettingsRow
            label="Role"
            description="Your permission level within the organization."
          >
            <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors duration-150">
              <User className="h-3 w-3" />
              {formatRole(user.role)}
            </span>
          </SettingsRow>

          <SettingsRow
            label="Organization"
            description="The organization your account belongs to."
          >
            <span className="text-sm text-muted-foreground">
              {user.organization.name}
            </span>
          </SettingsRow>
        </div>
      </Card>

      {/* ── Appearance ── */}
      <SectionLabel title="Appearance" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SettingsRow
            id="personal-theme"
            label="Theme"
            description="Use a light or dark theme, or match your system setting."
          >
            <Select value={theme} onValueChange={handleThemeChange}>
              <SelectTrigger className="w-[140px]" aria-label="Theme">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </SettingsRow>
        </div>
      </Card>

      {/* ── Status Report ── */}
      <SectionLabel title="Status Report" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <StatusReportPickerRow />
        </div>
      </Card>

      {/* ── Notifications ── */}
      <SectionLabel title="Notifications" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <QuoteViewNotificationsRow />
        </div>
      </Card>

      {/* ── Session ── */}
      <SectionLabel title="Session" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SettingsRow
            label="Sign Out"
            description="End your current session and return to the login page."
          >
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              disabled={isLoggingOut}
              className="transition-colors duration-150"
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" />
              Logout
              {isLoggingOut && (
                <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin" />
              )}
            </Button>
          </SettingsRow>
        </div>
      </Card>
    </div>
  );
}

// ── Local Components ──

/**
 * Skeleton loading state matching the PersonalSettingsPanel layout.
 * Renders placeholder rows for the Account and Session sections.
 * @returns Skeleton layout element
 */
function PersonalSettingsSkeleton() {
  return (
    <div className="space-y-6 pt-4">
      <Skeleton className="h-5 w-16 ml-2" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonRow key={`account-${i}`} />
          ))}
        </div>
      </Card>

      <Skeleton className="h-5 w-16 ml-2" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SkeletonRow />
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout dimensions.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-[200px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

// ── Utility Functions ──

/**
 * Formats a role string for display by capitalizing the first letter
 * and lowercasing the rest.
 * @param role - Raw role string (e.g. "ADMIN")
 * @returns Human-readable role string (e.g. "Admin")
 */
function formatRole(role: string): string {
  if (role.length === 0) {
    return "Member";
  }
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}
